import { InputSummaryItem } from '../../../../input-summary';
import InputSummaryUtils from '../../../../../helper/input-summary/input-summary-util';
import { UnitValue } from '../../../../data-structures/unit-value.model';

/**
 * An interface used to define the class structure for the feedstock bmp potential AI2 model
 */
export interface FeedstockBmpPotentialAI2Interface {
  lipidFat: UnitValue;
  protein: UnitValue;
  nonFibreCarbohydrate: UnitValue;
  ndf: UnitValue;
  adf: UnitValue;
  adl: UnitValue;
  ch4ContentEnabled: boolean;
  ch4Content: UnitValue;
}

/**
 * A class that contains members for the feedstock BMP potential AI2 model
 */
export class FeedstockBmpPotentialAI2 implements FeedstockBmpPotentialAI2Interface {
  lipidFat: UnitValue;
  protein: UnitValue;
  nonFibreCarbohydrate: UnitValue;
  ndf: UnitValue;
  adf: UnitValue;
  adl: UnitValue;
  ch4ContentEnabled: boolean;
  ch4Content: UnitValue;

  /**
   * constructs the FeedstockBmpPotentialElemental class.
   * @param param is used to construct the class. If null or not provided, the class will be set to default values
   */
  constructor(param: FeedstockBmpPotentialAI2Interface = {
    lipidFat: new UnitValue(),
    protein: new UnitValue(),
    nonFibreCarbohydrate: new UnitValue(),
    ndf: new UnitValue(),
    adf: new UnitValue(),
    adl: new UnitValue(),
    ch4ContentEnabled: false,
    ch4Content: new UnitValue()
  }) {
    this.lipidFat = new UnitValue(param.lipidFat);
    this.protein = new UnitValue(param.protein);
    this.nonFibreCarbohydrate = new UnitValue(param.nonFibreCarbohydrate);
    this.ndf = new UnitValue(param.ndf);
    this.adf = new UnitValue(param.adf);
    this.adl = new UnitValue(param.adl);
    this.ch4ContentEnabled = param.ch4ContentEnabled ?? false;
    this.ch4Content = new UnitValue(param.ch4Content);
  }

  /**
   * returns a separate instance of the provided FeedstockBmpPotentialAI2 obj
   * @param feedstockBmpPotentialAI2 will be returned as a separate instance
   */
  static deepCopy(feedstockBmpPotentialAI2: FeedstockBmpPotentialAI2) {
    if (!feedstockBmpPotentialAI2) {
      throw new Error('unable to perform deep copy of FeedstockBmpPotentialAI2');
    }
    return new FeedstockBmpPotentialAI2(feedstockBmpPotentialAI2);
  }

  /**
   * Returns an array of InputSummaryItem containing inputs for each member of the FeedstockBmpPotentialAI2 class
   */
  getSummaryInputs(path: string[]) {
    let inputs: InputSummaryItem[] = [];
    inputs = InputSummaryUtils.performPush(inputs, FeedstockBmpPotentialAI2PropName.lipidFat, this.lipidFat, [...path, 'lipidFat']);
    inputs = InputSummaryUtils.performPush(inputs, FeedstockBmpPotentialAI2PropName.protein, this.protein, [...path, 'protein']);
    inputs = InputSummaryUtils.performPush(inputs, FeedstockBmpPotentialAI2PropName.nonFibreCarbohydrate, this.nonFibreCarbohydrate, [...path, 'nonFibreCarbohydrate']);
    inputs = InputSummaryUtils.performPush(inputs, FeedstockBmpPotentialAI2PropName.ndf, this.ndf, [...path, 'ndf']);
    inputs = InputSummaryUtils.performPush(inputs, FeedstockBmpPotentialAI2PropName.adf, this.adf, [...path, 'adf']);
    inputs = InputSummaryUtils.performPush(inputs, FeedstockBmpPotentialAI2PropName.adl, this.adl, [...path, 'adl']);
    inputs = InputSummaryUtils.performPush(inputs, FeedstockBmpPotentialAI2PropName.ch4ContentEnabled, this.ch4ContentEnabled, [...path, 'ch4ContentEnabled']);
    if (this.ch4ContentEnabled) {
      inputs = InputSummaryUtils.performPush(inputs, FeedstockBmpPotentialAI2PropName.ch4Content, this.ch4Content, [...path, 'ch4Content']);
    }
    return inputs;
  }
}


/**
 * An enum that contains a proper display name for properties of the FeedstockBmpPotentialAI2 class.
 *
 */
export enum FeedstockBmpPotentialAI2PropName {
  lipidFat = 'INPUT_SUMMARY.FEEDSTOCK.BMP_POTENTIAL.AI2.LIPID_FAT',
  protein = 'INPUT_SUMMARY.FEEDSTOCK.BMP_POTENTIAL.AI2.PROTEIN',
  nonFibreCarbohydrate = 'INPUT_SUMMARY.FEEDSTOCK.BMP_POTENTIAL.AI2.NONFIBCARB',
  ndf = 'INPUT_SUMMARY.FEEDSTOCK.BMP_POTENTIAL.AI2.NDF',
  adf = 'INPUT_SUMMARY.FEEDSTOCK.BMP_POTENTIAL.AI2.ADF',
  adl = 'INPUT_SUMMARY.FEEDSTOCK.BMP_POTENTIAL.AI2.ADL',
  ch4ContentEnabled = 'INPUT_SUMMARY.FEEDSTOCK.BMP_POTENTIAL.AI2.CH4CONTENT_ENABLED',
  ch4Content = 'INPUT_SUMMARY.FEEDSTOCK.BMP_POTENTIAL.AI2.CH4CONTENT',
  id_lipidFat = 'lipidFat',
  id_protein = 'protein',
  id_nonFibreCarbohydrate = 'non-fibre carbohydrate',
  id_ndf = 'neutral detergent fiber',
  id_adf = 'acid detergent fiber',
  id_adl = 'acid detergent lignin',
  id_ch4ContentEnabled = 'CH4 content of biogas enabled',
  id_ch4Content = 'CH4 content of biogas'
}
